import { createContext, useContext, useEffect, useMemo, useReducer, useState } from 'react';
import { AUTH_TOKEN, AUTH_TOKEN_ADMIN, AUTH_TOKEN_WEB, AUTH_CONTEXT_KEYS } from '../config/constants';
import { getAllResourceProfile, getCampaign, getEnvironmentUI, getPopup, getProfileMeta } from '../api/resources';
import { getProfile } from '../api/profile';
import socket from '../socket';
import { useQuery, useQueryClient } from 'react-query';
import socketMaintenance from '../utils/socketMaintenance';
import cookie from 'js-cookie';
import axios from 'axios';
import { AppContext } from './AppContext';
import { useHistory } from 'react-router-dom';
import { useLocation } from 'react-use';

export const AuthContext = createContext({
  popups: [],
  campaigns: [],
  envUi: {},
  userProfile: {},
  profileMeta: [],
  resourceProfile: [],
});

export const AuthContextProvider = ({ children }) => {
  const [resourceProfile, setResourceProfile] = useState([]);
  const [profileMeta, setProfileMeta] = useState([]);
  const [userProfile, setUserProfile] = useState({});
  const [envUi, setEnvUi] = useState({});
  const [campaigns, setCampaigns] = useState([]);
  const [popups, setPopups] = useState([]);
  const history = useHistory();
  const isClient = typeof window !== 'undefined';
  const search = isClient ? window.location.search : {};
  const { pathname } = useLocation();
  const queryClient = useQueryClient();

  const appContext = useContext(AppContext);
  const isAuthenticated = appContext.isAuthenticated;

  //  if (!isAuthenticated && !window.location.pathname.indexOf(`${process.env.RAZZLE_PUBLIC_URL}`)) {
  //    window.location.href = `${process.env.RAZZLE_PUBLIC_URL}/error/404`;
  //    return;
  //  }

  const { data: envUiRaw } = useQuery('envUi', getEnvironmentUI, { cacheTime: Infinity });
  const { data: resourceProfileRaw } = useQuery('resourceProfile', getAllResourceProfile, {
    cacheTime: Infinity,
    enabled: !!isAuthenticated,
  });
  const { data: profileMetaRaw } = useQuery('profileMeta', getProfileMeta, {
    cacheTime: Infinity,
    enabled: !!isAuthenticated,
  });
  const { data: userProfileRaw } = useQuery('userProfile', getProfile, {
    cacheTime: Infinity,
    enabled: !!isAuthenticated,
  });
  const { data: campaignsRaw } = useQuery('campaigns', getCampaign, {
    cacheTime: Infinity,
    enabled: !!isAuthenticated,
  });
  const { data: popupsRaw } = useQuery('popups', getPopup, { cacheTime: Infinity, enabled: !!isAuthenticated });
  useEffect(() => {
    const params = new URLSearchParams(search);
    const isAdminProfile = params.get('isAdmin');

    if (isAdminProfile && !['/web/chat'].includes(pathname)) {
      const token = cookie.get(AUTH_TOKEN_ADMIN);
      if (token) {
        cookie.remove(AUTH_TOKEN);
        cookie.set(AUTH_TOKEN, token);
      }
    } else {
      if (['/web/chat'].includes(pathname)) {
        const requestConversationId = params.get('conversationId') ?? '';
        if (!isAdminProfile && !requestConversationId) return;

        const token = cookie.get(AUTH_TOKEN_ADMIN);
        if (!token) return;

        cookie.remove(AUTH_TOKEN);
        cookie.remove(AUTH_TOKEN_WEB);
        cookie.set(AUTH_TOKEN, token);
        cookie.set(AUTH_TOKEN_WEB, token);
        cookie.remove(AUTH_TOKEN_ADMIN);

        if (!isAdminProfile)
          Object.values(AUTH_CONTEXT_KEYS).forEach((e) => queryClient.invalidateQueries({ queryKey: e }));
        return;
      }

      const tokenWeb = cookie.get(AUTH_TOKEN_WEB);
      const token = cookie.get(AUTH_TOKEN);
      if (tokenWeb) {
        cookie.remove(AUTH_TOKEN);
        cookie.set(AUTH_TOKEN, tokenWeb);
      }
      if (!tokenWeb && token) {
        // cookie.remove(AUTH_TOKEN);
        cookie.remove('hidden_bottom_nav');
        cookie.remove('refreshToken');
        // history.push('/web/login');
      }
    }
  }, [search, pathname, queryClient]);

  useEffect(() => {
    if (resourceProfileRaw?.data) {
      setResourceProfile(resourceProfileRaw?.data || []);
    }
  }, [resourceProfileRaw]);

  useEffect(() => {
    if (profileMetaRaw?.data) {
      setProfileMeta(profileMetaRaw.data);
    }
  }, [profileMetaRaw]);

  useEffect(() => {
    if (userProfileRaw?.data) {
      setUserProfile(userProfileRaw.data);
    }
  }, [userProfileRaw]);

  useEffect(() => {
    if (envUiRaw?.data) {
      setEnvUi(envUiRaw.data);
    }
  }, [envUiRaw]);

  useEffect(() => {
    if (campaignsRaw?.data) {
      setCampaigns(campaignsRaw.data);
    }
  }, [campaignsRaw]);

  useEffect(() => {
    if (popupsRaw?.data) {
      setPopups(popupsRaw.data);
    }
  }, [popupsRaw]);

  useEffect(() => {
    const onSocket = () => {
      let token = cookie.get(AUTH_TOKEN);
      let connected = false;
      const RETRY_INTERVAL = 15000;
      let timeout;
      let isDisconnect = false;

      //Start connect socket
      socket.on('connect', function () {
        clearTimeout(timeout);
        // if (isDisconnect) {
        //   window.location.reload();
        // }
      });

      // Start reconnect if socket is disconnect
      socket.on('disconnect', function (err) {
        connected = false;
        isDisconnect = true;
        retryConnectOnFailure(RETRY_INTERVAL);
      });

      //Reconnect function
      let retryConnectOnFailure = function (retryInMilliseconds) {
        setTimeout(function () {
          if (!connected) {
            axios.get(`${process.env.RAZZLE_REACT_APP_API_DOMAIN}/ping`).then((data) => {});
            retryConnectOnFailure(retryInMilliseconds);
          }
        }, retryInMilliseconds);
      };
      socket
        .emit('authenticate', {
          token: token,
        })
        .on('authenticated', () => {
          // setSocketAuthenticated(true);
        })
        .on('unauthorized', (msg) => {
          let refreshToken = cookie.get('refreshToken');
          if (!refreshToken) {
            return;
          }

          // Template comment
          // if (refreshToken) {
          //   requestAccessToken(refreshToken).then(reponse => {
          //     const newToken = reponse?.data?.data?.token;
          //     if (!newToken) return;
          //     cookie.set(AUTH_TOKEN, newToken);
          //     socket
          //       .emit('authenticate', {
          //         token: newToken,
          //       })
          //       .on('authenticated', () => {
          //         // setSocketAuthenticated(true);
          //       });
          //   });
          // }
        });

      // Start connection
      socket.connect();
      retryConnectOnFailure(RETRY_INTERVAL);
    };

    if (isAuthenticated) {
      onSocket();
    }
    typeof window !== 'undefined' && localStorage.removeItem('redirect_home');
  }, [isAuthenticated]);

  useEffect(() => {
    socketMaintenance();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        resourceProfile,
        profileMeta,
        userProfile,
        envUi,
        campaigns,
        popups,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
